import React, { useState } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionItemBody,
  AccordionItemHeader,
  ActionList,
  ActionListItem,
  Alert,
  Amount,
  Badge,
  Box,
  Button,
  CheckCircleIcon,
  ChevronDownIcon,
  Drawer,
  DrawerBody,
  DrawerHeader,
  Dropdown,
  DropdownButton,
  DropdownOverlay,
  Link,
  DownloadIcon,
  RefreshIcon,
  Skeleton,
  StepGroup,
  StepItem,
  StepItemIndicator,
  Text,
  XCircleIcon,
  useToast,
  AlertTriangleIcon,
} from '@razorpay/blade/components';
import { EmptyView } from 'components/SalaryComponents/components/EmptyView';
import { DetailsSkeleton } from 'components/ui/Skeletons/DetailsSkeleton';
import { useQuery } from 'react-query';
import api from 'api';
import {
  LOAN_STATUS_COLOR,
  LOAN_STATUS_LABEL,
  LOAN_STATUS,
} from 'components/LoansAndAdvances/constants';
import { ApproveApplicationModal } from './ApproveApplicationModal';
import { EditApproveModal } from './EditApproveModal';
import { RejectApplicationModal } from './RejectApplicationModal';
import { ActiveModal, BasicUserLoanData, LoanDetail, LoansPaymentDrawerProps } from './types';
import {
  ADMIN_LOAN_DETAIL_KEY,
  BADGE_TO_ICON_COLOR,
  BASIC_USER_LOAN_DATA_DRAWER_KEY,
  INTEREST_TYPE_LABEL,
  LOAN_MODAL,
} from './constants';
import {
  formatDate,
  formatMonthYear,
  computeEligibilityChecks,
  normalizeLoanDetail,
} from './utils';
import { DetailRow } from './DetailRow';

const StatusIcon = ({ status }: { status: number }) => {
  const badgeColor = LOAN_STATUS_COLOR[status] ?? 'neutral';
  const iconColor = (BADGE_TO_ICON_COLOR[badgeColor] ?? 'surface.icon.gray.normal') as Parameters<
    typeof CheckCircleIcon
  >[0]['color'];
  return status === LOAN_STATUS.REJECTED ? (
    <XCircleIcon color={iconColor} size="2xlarge" />
  ) : (
    <CheckCircleIcon color={iconColor} size="2xlarge" />
  );
};
const DocumentLink = ({ fileId }: { fileId: string }) => {
  const { show: showToast } = useToast();
  const [isLoading, setIsLoading] = React.useState(false);

  const handleClick = async () => {
    setIsLoading(true);
    try {
      const { signed_url } = await api.loans.downloadLoanDocument(fileId);
      window.open(signed_url, '_blank', 'noopener,noreferrer');
    } catch {
      showToast({
        content: 'Failed to fetch document. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Link
      icon={DownloadIcon}
      iconPosition="left"
      onClick={handleClick}
      size="small"
      isDisabled={isLoading}>
      {isLoading ? 'Loading…' : 'View Document'}
    </Link>
  );
};

export const LoansPaymentDrawer = ({ isOpen, onDismiss, loanId }: LoansPaymentDrawerProps) => {
  const { show: showToast } = useToast();
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const capturedLoanRef = React.useRef<LoanDetail | null>(null);

  const {
    data: loanData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: [ADMIN_LOAN_DETAIL_KEY, loanId],
    queryFn: () => api.loans.getLoanDetail(loanId!),
    enabled: !!loanId && isOpen,
  });

  const loan = normalizeLoanDetail<LoanDetail>(loanData);
  if (loan) capturedLoanRef.current = loan;

  const { data: basicUserData } = useQuery<BasicUserLoanData>({
    queryKey: [BASIC_USER_LOAN_DATA_DRAWER_KEY, loan?.people_id],
    queryFn: () => api.loans.basicUserLoanData(loan!.people_id),
    enabled: !!loan?.people_id && isOpen,
  });
  const eligibilityChecks = computeEligibilityChecks(basicUserData, loan?.monthly_total_emi ?? 0);
  const eligibilityPassedCount = eligibilityChecks.filter((c) => c.passed).length;

  return (
    <>
      <Drawer isOpen={isOpen} onDismiss={onDismiss}>
        {!isLoading ? (
          <DrawerHeader title="Request" color={LOAN_STATUS_COLOR[loan?.status ?? -1] ?? 'neutral'}>
            <>
              {isLoading && (
                <Box
                  paddingX="spacing.4"
                  paddingY="spacing.5"
                  display="flex"
                  flexDirection="column"
                  gap="spacing.4">
                  <Skeleton height="spacing.7" width="50%" />
                  <Skeleton height="spacing.7" />
                </Box>
              )}
              {loan != null && (
                <Box
                  display="flex"
                  flexDirection="column"
                  gap="spacing.4"
                  paddingTop="spacing.5"
                  width="100%">
                  <Box display="flex" flexDirection="column" alignItems="center" gap="spacing.4">
                    <StatusIcon status={loan.status} />
                    <Amount
                      value={loan.principal}
                      size="2xlarge"
                      type="heading"
                      weight="semibold"
                    />
                    <Badge color={LOAN_STATUS_COLOR[loan.status] ?? 'neutral'}>
                      {LOAN_STATUS_LABEL[loan.status] ?? 'Unknown'}
                    </Badge>
                    {loan.status === LOAN_STATUS.PENDING && (
                      <Box display="flex" gap="spacing.3" width="100%">
                        <Box flex="1" display="flex" borderRadius="medium" overflow="hidden">
                          <Box flex="1">
                            <Button
                              isFullWidth
                              onClick={() => {
                                setActiveModal(LOAN_MODAL.APPROVE);
                              }}>
                              Approve
                            </Button>
                          </Box>
                          <Box borderLeftWidth="thinner" borderColor="surface.border.gray.subtle">
                            <Dropdown>
                              <DropdownButton icon={ChevronDownIcon} />
                              <DropdownOverlay defaultPlacement="bottom-end">
                                <ActionList>
                                  {[
                                    <ActionListItem
                                      key="edit-approve"
                                      title="Edit & Approve"
                                      value="edit_approve"
                                      onClick={() => {
                                        setActiveModal(LOAN_MODAL.EDIT_APPROVE);
                                      }}
                                    />,
                                  ]}
                                </ActionList>
                              </DropdownOverlay>
                            </Dropdown>
                          </Box>
                        </Box>
                        <Box flex="1">
                          <Button
                            isFullWidth
                            color="negative"
                            variant="secondary"
                            onClick={() => {
                              setActiveModal(LOAN_MODAL.REJECT);
                            }}>
                            Reject
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                  {loan.status === LOAN_STATUS.REJECTED && loan.rejection_note && (
                    <Alert
                      isFullWidth
                      color="negative"
                      isDismissible={false}
                      description={`Reason: ${loan.rejection_note}`}
                      icon={AlertTriangleIcon}
                    />
                  )}
                  <Text size="small" color="surface.text.gray.muted" textAlign="center">
                    #{loan.id} • Requested on {formatDate(loan.created_at)}
                  </Text>
                </Box>
              )}
            </>
          </DrawerHeader>
        ) : null}

        <DrawerBody>
          {isLoading && (
            <Box paddingX="spacing.4" display="flex" flexDirection="column" gap="spacing.6">
              <DetailsSkeleton rowCount={6} />
            </Box>
          )}
          {isError && (
            <EmptyView
              isError
              description={
                <Text color="surface.text.gray.muted" weight="medium" textAlign="center">
                  Could not load loan details. Please try again.
                </Text>
              }>
              <Box display="flex" justifyContent="center">
                <Button icon={RefreshIcon} size="small" onClick={() => refetch()}>
                  Retry
                </Button>
              </Box>
            </EmptyView>
          )}
          {loan && (
            <Box display="flex" flexDirection="column" gap="spacing.4">
              {(loan.status === LOAN_STATUS.APPROVED || loan.status === LOAN_STATUS.REJECTED) && (
                <Box backgroundColor="surface.background.gray.moderate">
                  <Accordion defaultExpandedIndex={0}>
                    <AccordionItem>
                      <AccordionItemHeader title="Timeline" />
                      <AccordionItemBody>
                        <StepGroup orientation="vertical" size="medium">
                          <StepItem
                            title="Request Approval"
                            description="By Admin"
                            marker={
                              <StepItemIndicator
                                color={
                                  loan.status === LOAN_STATUS.REJECTED ? 'negative' : 'positive'
                                }
                              />
                            }
                            stepProgress={loan.status === LOAN_STATUS.APPROVED ? 'full' : 'none'}
                          />
                          <StepItem
                            title="Request Submitted"
                            description={`By ${loan.name ?? 'Employee'}`}
                            marker={<StepItemIndicator color="positive" />}
                            stepProgress="full"
                          />
                        </StepGroup>
                      </AccordionItemBody>
                    </AccordionItem>
                  </Accordion>
                </Box>
              )}

              {loan.status === LOAN_STATUS.PENDING && eligibilityChecks.length > 0 && (
                <Box backgroundColor="surface.background.gray.moderate">
                  <Accordion defaultExpandedIndex={0}>
                    <AccordionItem>
                      <AccordionItemHeader
                        title="Eligibility Checks"
                        trailing={
                          <Text size="medium" variant="body" weight="semibold">
                            ({eligibilityPassedCount}/{eligibilityChecks.length})
                          </Text>
                        }
                      />
                      <AccordionItemBody>
                        <Box display="flex" flexDirection="column" gap="spacing.5">
                          {eligibilityChecks.map((check) => (
                            <Box
                              key={check.label}
                              display="flex"
                              alignItems="center"
                              gap="spacing.3">
                              {check.passed ? (
                                <CheckCircleIcon
                                  color="feedback.icon.positive.intense"
                                  size="medium"
                                />
                              ) : (
                                <XCircleIcon color="feedback.icon.negative.intense" size="medium" />
                              )}
                              <Text size="small" color="surface.text.gray.subtle">
                                {check.label}
                              </Text>
                            </Box>
                          ))}
                        </Box>
                      </AccordionItemBody>
                    </AccordionItem>
                  </Accordion>
                </Box>
              )}

              {loan.status === LOAN_STATUS.APPROVED && (
                <Box backgroundColor="surface.background.gray.moderate">
                  <Accordion defaultExpandedIndex={0}>
                    <AccordionItem>
                      <AccordionItemHeader title="Advance Details" />
                      <AccordionItemBody>
                        <Box display="flex" flexDirection="column" gap="spacing.5">
                          <DetailRow
                            label="Amount"
                            value={<Amount value={loan.principal} size="small" />}
                          />
                          <DetailRow label="Repayment Tenure" value={`${loan.tenure} Months`} />
                          <DetailRow
                            label="EMI Start Month"
                            value={formatMonthYear(loan.recovery_start_from)}
                          />
                          <DetailRow
                            label="Interest Type"
                            value={
                              loan.interest_type != null
                                ? INTEREST_TYPE_LABEL[loan.interest_type] ?? '—'
                                : '—'
                            }
                          />
                          <DetailRow
                            label="Rate of Interest"
                            value={`${loan.interest_rate ? loan.interest_rate : 0}%`}
                          />
                          <DetailRow
                            label="Perquisite"
                            value={`${loan.perk_rate ? loan.perk_rate : 0}%`}
                          />
                          {loan.monthly_total_emi != null && (
                            <DetailRow
                              label="Monthly EMI"
                              value={<Amount value={loan.monthly_total_emi} size="small" />}
                            />
                          )}
                        </Box>
                      </AccordionItemBody>
                    </AccordionItem>
                  </Accordion>
                </Box>
              )}

              <Box backgroundColor="surface.background.gray.moderate">
                <Accordion defaultExpandedIndex={0}>
                  <AccordionItem>
                    <AccordionItemHeader title={'Request Details'} />
                    <AccordionItemBody>
                      <Box display="flex" flexDirection="column" gap="spacing.5">
                        <DetailRow
                          label="Amount"
                          value={<Amount value={loan.principal} size="small" />}
                        />
                        <DetailRow label="Request ID" value={`#${loan.id}`} />
                        <DetailRow
                          label="Requested on"
                          value={loan.created_at ? formatDate(loan.created_at) : '—'}
                        />
                        <DetailRow
                          label="Reason for Request"
                          value={loan.loan_category_name ?? '—'}
                        />
                        {loan.remarks && <DetailRow label="Specific Reason" value={loan.remarks} />}
                        <DetailRow
                          label="Documents"
                          value={
                            loan.document_file_id ? (
                              <DocumentLink fileId={loan.document_file_id} />
                            ) : (
                              '—'
                            )
                          }
                        />
                        <DetailRow label="Repayment Tenure" value={`${loan.tenure} Months`} />
                        {loan.recovery_start_from && (
                          <DetailRow
                            label="Deduction Start Month"
                            value={formatMonthYear(loan.recovery_start_from)}
                          />
                        )}
                      </Box>
                    </AccordionItemBody>
                  </AccordionItem>
                </Accordion>
              </Box>
              {loan.status === LOAN_STATUS.PENDING && (
                <Box backgroundColor="surface.background.gray.moderate">
                  <Accordion defaultExpandedIndex={0}>
                    <AccordionItem>
                      <AccordionItemHeader title="Salary Impact Analysis" />
                      <AccordionItemBody>
                        <Box display="flex" flexDirection="column" gap="spacing.5">
                          {loan.monthly_total_emi != null && (
                            <DetailRow
                              label="New Monthly EMI"
                              value={<Amount value={loan.monthly_total_emi} size="small" />}
                            />
                          )}
                          {loan.remaining_amount_to_be_paid != null && (
                            <DetailRow
                              label="Outstanding Amount"
                              value={
                                <Amount value={loan.remaining_amount_to_be_paid} size="small" />
                              }
                            />
                          )}
                        </Box>
                      </AccordionItemBody>
                    </AccordionItem>
                  </Accordion>
                </Box>
              )}
            </Box>
          )}
        </DrawerBody>
      </Drawer>

      <ApproveApplicationModal
        isOpen={activeModal === LOAN_MODAL.APPROVE}
        onDismiss={() => setActiveModal(null)}
        loanId={loanId}
        onSuccess={onDismiss}
      />

      <EditApproveModal
        isOpen={activeModal === LOAN_MODAL.EDIT_APPROVE}
        onClose={() => setActiveModal(null)}
        loan={capturedLoanRef.current}
        onSuccess={(msg) => {
          showToast({ content: msg, color: 'positive', duration: 4000 });
          onDismiss();
        }}
      />

      <RejectApplicationModal
        isOpen={activeModal === LOAN_MODAL.REJECT}
        onDismiss={() => setActiveModal(null)}
        loanId={loanId}
        onSuccess={onDismiss}
      />
    </>
  );
};
